/**
 * Canonical SpotMe corporate WhatsApp number — the destination for EVERY
 * customer-facing WA CTA on every surface (listing pages, /reservar,
 * SpecialistCard, broker hero CTAs, floating FAB, anfitrion/host CTAs,
 * /flotillas, /tradicional, the directory ficha's claim-section shortcut,
 * etc.). Update here if the number ever changes.
 *
 * v1 routing contract — read before considering any "direct to specialist"
 * change:
 *
 *   - Specialists (Fernando, Francisco, ...) DO have phone
 *     numbers stored on the server — see `OWNERS` in
 *     `spotme-server/src/datasources/HubSpot/constants.ts` and the
 *     `Specialist.whatsappE164` GraphQL field. The server already knows
 *     who would receive each lead.
 *   - BUT no client-side surface routes directly to those phones. Every
 *     WA tap lands on this corporate number, where the LangGraph chatbot
 *     triages, qualifies, creates the HubSpot deal, and routes ownership
 *     to the right specialist. The human-to-human handoff happens AFTER
 *     the chatbot has done its work.
 *   - Why this shape (don't break it without replacing all three):
 *       (a) it closes the server-side `qualified_whatsapp_lead` loop
 *           (LangGraph `complete` → captureServerEvent + Google Ads /
 *           Meta CAPI enhanced conversions). Direct wa.me to a specialist
 *           bypasses this — Smart Bidding loses its deepest signal.
 *       (b) it protects specialists from leaked personal phones in any
 *           public surface (screenshots, listing pages, /reservar, etc).
 *       (c) it gives sales a single triage funnel — spam filter, load
 *           balancing, per-thread dedupe (`qualifiedLeadEventSent`).
 *   - When sales-ops is provisioned to absorb un-triaged inbound, the
 *     unlock path is:
 *       1. Plumb `Specialist.whatsappE164` into `<SpecialistCard>` (and
 *          any other specialist-bearing surface) as a `recipientNumber`
 *          prop.
 *       2. Pass it through to `buildWhatsappUrl(message, recipientNumber)`
 *          as the 2nd arg — the helper already accepts it.
 *       3. Reproduce the `qualified_whatsapp_lead` server-side fire from
 *          a client-side click hook OR accept the analytics regression
 *          on that surface only.
 *       4. Update the "WhatsApp routing — trust face vs destination
 *          number" rule in `spotme-client/CLAUDE.md`.
 *
 * The trust-face/destination split (specialist's photo on the card but
 * conversation lands here) is INTENTIONAL. Don't treat it as a bug or
 * "fix" it by quietly switching one surface to specialist-direct routing.
 *
 * Line 1 (+52 81 1821 6319) since 2026-11-02 — the WhatsApp Cloud API line
 * the chatbot answers for everyone (spotme-server/docs/whatsapp-hubspot-exit.md
 * §0C). Since 2026-11-03 it is ALSO the public voice number: its SIM rings a
 * work phone, so a `tel:+528118216319` link is fine. A WhatsApp CALL to it
 * still does not connect (Cloud API). +52 55 5930 1159 is no longer public.
 */
export const SPOTME_WHATSAPP_NUMBER = '528118216319'

/** Human-readable form of `SPOTME_WHATSAPP_NUMBER` for printed WA hints. */
export const SPOTME_WHATSAPP_DISPLAY = '+52 81 1821 6319'
const WHATSAPP_BASE_URL = 'https://api.whatsapp.com/send'

/**
 * Build a `https://api.whatsapp.com/send?phone=...` URL with an optional
 * prefilled message and an optional explicit recipient number. Used by the
 * broker CTAs on B2B pillar landing pages, the floating WhatsApp button,
 * specialist routing, and any "talk to a specialist" affordance.
 *
 * Why `api.whatsapp.com/send` instead of `wa.me`: both work, but the
 * `wa.me` short-link drops the prefill into the iOS WhatsApp draft input,
 * which has historically collapsed `%0A%0A` (blank lines) to spaces on
 * focus on some versions. `api.whatsapp.com/send` routes through the
 * WhatsApp Web confirmation screen first, which preserves newlines more
 * reliably across clients. Multi-line lead-form prefills (see
 * `buildLeadFormWaMessage`) depend on this for legible delivery.
 *
 * Session-token attribution note: this helper deliberately does NOT
 * embed the PostHog session token. For listing-bound CTAs (lead form,
 * specialist card) the token rides via the listing URL as `?sid={token}`
 * — see `buildLeadFormWaMessage`. For bare CTAs (floating FAB, contact
 * page, host nav) the customer sees the prefill draft before sending,
 * and a visible `| esvk` suffix reads as a tracking marker — bad UX,
 * minimal upside. Server-side `findWhatsappAttribution` falls back to
 * phone-based stitching for those leads, so the `qualified_whatsapp_lead`
 * loop still closes via HubSpot / Google Ads enhanced conversions even
 * without PostHog session linkage.
 *
 * @param message - Optional pre-filled message text (URL-encoded internally).
 *                  Pass undefined / empty to get the bare number link (the
 *                  user starts the conversation themselves).
 * @param recipientNumber - Optional E.164 number (with or without leading '+').
 *                          When omitted, falls back to SPOTME_WHATSAPP_NUMBER.
 */
export function buildWhatsappUrl(
  message?: string,
  recipientNumber?: string
): string {
  const number = (recipientNumber ?? SPOTME_WHATSAPP_NUMBER).replace(/^\+/, '')
  if (!message || message.trim().length === 0) {
    return `${WHATSAPP_BASE_URL}?phone=${number}`
  }
  return `${WHATSAPP_BASE_URL}?phone=${number}&text=${encodeURIComponent(message)}`
}

/**
 * Build a no-recipient WhatsApp share URL — the user picks the recipient
 * after WhatsApp opens. Use for "share with a friend" / referral flows
 * where SpotMe is NOT the recipient; for talk-to-SpotMe CTAs use
 * `buildWhatsappUrl` (which defaults to the corporate number).
 *
 * @param message - Pre-filled message text (URL-encoded internally).
 *                  Empty / missing returns a bare share URL that opens
 *                  WhatsApp's recipient picker with no draft text.
 */
export function buildWhatsappShareUrl(message?: string): string {
  if (!message || message.trim().length === 0) {
    return WHATSAPP_BASE_URL
  }
  return `${WHATSAPP_BASE_URL}?text=${encodeURIComponent(message)}`
}

/**
 * Skip-hint regex — if the prefill already contains a SpotMe URL, the
 * listing path itself carries the city (`/renta-mini-bodegas/monterrey/...`)
 * and a `· mty` suffix on top of that is pure redundancy. Matches both
 * `www.spotme.mx/` and bare `spotme.mx/` to cover lead-form prefills,
 * listing-FAB messages, and any future caller that embeds a listing
 * URL in the WA draft.
 */
const SPOTME_URL_RE = /(?:www\.)?spotme\.mx\//i

/**
 * Append a compact city-hint suffix (`… · mty`) to a bare-CTA WhatsApp
 * prefill so the LangGraph agent and the human sales team start the
 * conversation with the user's approximate metro in mind, without
 * bloating the customer-visible draft.
 *
 * Format: `<message> · <code>` where `<code>` is one of `mty` / `cdmx` /
 * `gdl` / `spgg` / ... from `src/constants/cityHintCodes.ts` (mirrored
 * on the server). The codes match common Mexican abbreviations so the
 * suffix reads as a city marker to a Mexican user, not random tracking
 * garbage like the old `· esvk` session-token suffix we removed.
 *
 * Why a HINT, not a confirmed claim: the code comes from browser geo
 * (precise but opt-in) or IP lookup (city-level, wrong ~20-30% via VPN
 * / carrier NAT / business WiFi). The LangGraph analysis prompt decodes
 * the suffix via the same table and surfaces the city as an approximate
 * hint — the compose prompts then ASK ("¿estás buscando en X o en otra
 * ciudad?") instead of asserting it.
 *
 * The helper is self-protecting against redundancy: it returns the
 * message unchanged when (a) no hint code is available, (b) the message
 * is empty/missing (a bare `· mty` draft is worse UX than no draft at
 * all — the customer would see only the code), or (c) the message
 * already contains a SpotMe URL whose path carries the city
 * (`/renta-mini-bodegas/monterrey/...`). The listing FAB on every detail
 * page (`Details/WrapperWA.tsx`) hits case (c) — without the URL guard
 * a Monterrey listing's draft would end with `· mty` next to a path
 * already containing `monterrey`, doubling the same information.
 *
 * @param message - The base prefill (e.g. `t('HOST_CTA.WHATSAPP_MSG')`).
 * @param cityHintCode - The compact code from `useWhatsappCityHint()`.
 *                       Empty string means we have no signal — return
 *                       the message unchanged.
 */
export function appendCityHint(
  message: string | undefined,
  cityHintCode: string
): string | undefined {
  if (!cityHintCode) return message
  if (!message || message.trim().length === 0) return message
  if (SPOTME_URL_RE.test(message)) return message
  return `${message} · ${cityHintCode}`
}
